import { useState } from 'react';
import { X, CreditCard, Shield, Loader2 } from 'lucide-react';
import { createPayment, PLANS } from '../../services/paymentService';

const PaymentModal = ({ isOpen, onClose, planType, user }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const plan = PLANS[planType];

    if (!isOpen || !plan) return null;

    const handlePayment = async () => {
        if (!user?.peopleUuid) {
            setError('Vous devez être connecté pour souscrire à un abonnement.');
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const result = await createPayment(user.peopleUuid, planType);

            if (result.success && result.data?.paymentUrl) {
                // Redirect to Mollie checkout
                window.location.href = result.data.paymentUrl;
            } else {
                setError(result.message || 'Erreur lors de la création du paiement.');
            }
        } catch (err) {
            console.error('Payment error:', err);
            setError(err.message || 'Une erreur est survenue. Veuillez réessayer.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
                {/* Header */}
                <div className="bg-linear-to-r from-linkoo-purple to-linkoo-pink p-6 text-white">
                    <div className="flex justify-between items-start">
                        <div>
                            <h2 className="text-2xl font-bold">Abonnement {plan.name}</h2>
                            <p className="text-white/80 mt-1">{plan.description}</p>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-1 hover:bg-white/20 rounded-full transition-colors"
                        >
                            <X className="w-6 h-6" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6">
                    {/* Price */}
                    <div className="text-center mb-6">
                        <span className="text-4xl font-bold text-gray-900">{plan.price}€</span>
                        <span className="text-gray-500">{plan.period}</span>
                    </div>

                    {/* Features summary */}
                    <div className="bg-gray-50 rounded-xl p-4 mb-6">
                        <h3 className="font-semibold text-gray-900 mb-3">Ce que vous obtenez :</h3>
                        <ul className="grid grid-cols-2 gap-x-4 gap-y-2">
                            {plan.features.filter(f => f.included).map((feature, index) => (
                                <li key={index} className="flex items-center gap-2 text-sm text-gray-600">
                                    <div className="shrink-0 w-4 h-4 rounded-full bg-green-100 flex items-center justify-center">
                                        <svg className="w-2.5 h-2.5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    {feature.text}
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Error message */}
                    {error && (
                        <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm">
                            {error}
                        </div>
                    )}

                    {/* Payment button */}
                    <button
                        onClick={handlePayment}
                        disabled={isLoading}
                        className="w-full bg-linear-to-r from-linkoo-purple to-linkoo-pink text-white py-3 px-6 rounded-xl font-semibold flex items-center justify-center gap-2 hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isLoading ? (
                            <>
                                <Loader2 className="w-5 h-5 animate-spin" />
                                Redirection vers le paiement...
                            </>
                        ) : (
                            <>
                                <CreditCard className="w-5 h-5" />
                                Payer {plan.price}€
                            </>
                        )}
                    </button>

                    {/* Security note */}
                    <div className="flex items-center justify-center gap-2 mt-4 text-xs text-gray-500">
                        <Shield className="w-4 h-4" />
                        <span>Paiement sécurisé par Mollie</span>
                    </div>

                    {/* Payment methods */}
                    <div className="flex items-center justify-center gap-3 mt-4">
                        <img src="https://www.mollie.com/external/icons/payment-methods/bancontact.svg" alt="Bancontact" className="h-6" />
                        <img src="https://www.mollie.com/external/icons/payment-methods/creditcard.svg" alt="Credit Card" className="h-6" />
                        <img src="https://www.mollie.com/external/icons/payment-methods/ideal.svg" alt="iDEAL" className="h-6" />
                        <img src="https://www.mollie.com/external/icons/payment-methods/paypal.svg" alt="PayPal" className="h-6" />
                    </div>
                </div>

                {/* Footer */}
                <div className="bg-gray-50 px-6 py-4 text-center">
                    <p className="text-xs text-gray-500">
                        En continuant, vous acceptez nos{' '}
                        <a href="/conditions" className="text-linkoo-purple hover:underline">conditions générales</a>
                        {' '}et notre{' '}
                        <a href="/confidentialite" className="text-linkoo-purple hover:underline">politique de confidentialité</a>.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default PaymentModal;
